/* eslint-disable import/prefer-default-export */
import { keys, includes, forEach } from 'lodash'
import type { FilterState } from '@src/redux/reducers/filter'
// eslint-disable-next-line import/named

interface OAuthToken {
  oauthToken?: string
}
export type Token = OAuthToken

interface BrowserAppInfo {
  isTokenExpired?: boolean
  redirectToPage?: string
  camSidebarCollapsed?: boolean
  camSetupStep?: string
  camCompletedSteps?: string
  filter?: FilterState
}

export interface AuthInfo extends Token {
  tokenType?: keyof Token
  refreshToken?: string
  userEmail?: string
}

interface IAppStorage extends BrowserAppInfo, AuthInfo {}

enum mapTypes {
  string = 'string',
  number = 'number',
  boolean = 'boolean',
  object = 'object'
}
export class AppStorage<T = IAppStorage> {
  private prefix: string
  private storage: Storage
  constructor(prefix: string, private baseStorage: Storage) {
    this.assertPresent(prefix)
    this.assertPresent(baseStorage)
    this.storage = baseStorage
    this.prefix = prefix
  }

  private assertPresent = (obj, fields?: string[]) => {
    if (obj === undefined) {
      throw new Error()
    }
    fields = fields || []
    const undef = fields.filter(f => obj[f] === undefined)
    if (undef.length) {
      throw new Error(`${undef[0]} is not present on {obj}.`)
    }
  }

  private getPrefix = () => (this.prefix ? `${this.prefix}.` : '')

  private typeMap = item => {
    const type = typeof item
    switch (type) {
      case mapTypes.string:
        return item as string
      case mapTypes.number:
        return item as number
      case mapTypes.boolean:
        return item as boolean
      case mapTypes.object:
        return item as object
      default:
        return item as any
    }
  }

  get = <K extends keyof T>(key: K): T[K] => {
    let item = this.storage.getItem(`${this.getPrefix()}${key}`)
    try {
      item = JSON.parse(item)
    } catch (error) {
      return this.typeMap(item)
    }
    return this.typeMap(item)
  }

  set = <K extends keyof T, V extends T[K]>(key: K, val: V) => {
    const toSave = typeof val === 'object' ? JSON.stringify(val) : val
    return this.storage.setItem(`${this.getPrefix()}${key}`, toSave as string)
  }

  remove = <K extends keyof T>(key: K) => {
    this.storage.removeItem(`${this.getPrefix()}${key}`)
  }

  empty = () => {
    keys(this.storage || {}).forEach(key => {
      if (key.startsWith(this.prefix)) {
        this.storage.removeItem(key)
      }
    })
  }

  purge = () => {
    this.storage.clear()
  }

  purgeJunk = () => {
    const storage: Storage = { ...this.baseStorage }
    this.storage.clear()
    forEach(storage, (value, key) => {
      if (includes(String(key), this.getPrefix())) {
        this.storage.setItem(String(key), value)
      }
    })
  }
}
